/**
 * Signed form identifiers (Phase D7 Unit C). A visitor's public POST to
 * `/public/forms` carries an opaque `formId` token minted by the render-side
 * deploy layer (E) and embedded in the rendered page.
 *
 * WHAT THE TOKEN IS — and IS NOT:
 *  - It is UNFORGEABLE and TAMPER-PROOF: the HMAC binds (siteId, instanceId)
 *    so a client cannot alter either half or fabricate a token for a form it
 *    was not served. That is the ONLY security property it provides.
 *  - The payload is AUTHENTICATED, NOT ENCRYPTED. siteId and instanceId are
 *    NOT secrets — they are already derivable from the served page (the
 *    instance anchor is in the HTML, the site is the page's own host), so the
 *    token deliberately does not try to hide them and nothing downstream may
 *    treat them as confidential.
 *  - To a CLIENT the token is an INDIVISIBLE OPAQUE BLOB: never parse, split,
 *    or interpret it client-side. Only the server (holding the secret) may
 *    decode it, and only via `verifyFormId`.
 *
 * token = base64url(payload) + '.' + base64url(HMAC_SHA256(payload, secret)),
 * where payload = `${siteId}\n${instanceId}`. Verification recomputes the
 * HMAC over the DECODED payload and compares it against the decoded
 * signature in CONSTANT TIME — `crypto.timingSafeEqual` only ever runs on
 * two equal-length buffers (a length mismatch is rejected first), mirroring
 * the discipline `api/hmac.ts` applies to its own (hex-encoded) signatures.
 * This file is intentionally self-contained rather than importing from
 * hmac.ts: that module's constant-time helper is shaped for hex strings,
 * this token is base64url, and Unit C's edit scope does not include hmac.ts.
 */
import { createHmac, timingSafeEqual } from 'node:crypto';

/** Bound checked BEFORE any decode/HMAC work — caps the cost of a
 *  garbage/oversized token thrown at the public, unauthenticated endpoint. */
export const MAX_FORM_ID_TOKEN_LENGTH = 512;

/** Minimum length for the HMAC secret that signs/verifies a form id — 32
 *  chars is the floor at which the key resists brute force. Enforced by a
 *  caller at construction time (`createApp` refuses to build `/public/forms`
 *  with a shorter one); mint/verify above do not re-check it themselves. */
export const MIN_FORM_ID_SECRET_LENGTH = 32;

/** base64url has no padding and uses only these characters; anything else in
 *  a segment is a malformed token, rejected before it is even decoded. */
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;

export interface VerifiedFormId {
  siteId: string;
  instanceId: string;
}

function encodePayload(siteId: string, instanceId: string): string {
  return `${siteId}\n${instanceId}`;
}

function signPayload(secret: string, payload: string): Buffer {
  return createHmac('sha256', secret).update(payload, 'utf-8').digest();
}

/**
 * Mint a signed opaque form identifier for (siteId, instanceId). The
 * render-side deploy layer (E) embeds the result in the rendered page.
 *
 * THROWS (loudly — mint runs server-side at render/deploy, where a bad id is
 * a provisioning bug to surface immediately, never to swallow) when:
 *  - siteId or instanceId is empty; or
 *  - either contains a '\n' (the payload field separator — a newline would
 *    let one field's tail be misread as the other, so it is structurally
 *    illegal, not merely malformed); or
 *  - the composed token would exceed MAX_FORM_ID_TOKEN_LENGTH (verifyFormId
 *    rejects anything longer, so an un-verifiable token must never be minted).
 */
export function mintFormId(secret: string, siteId: string, instanceId: string): string {
  if (!siteId || !instanceId) throw new Error('mintFormId: siteId and instanceId must be non-empty');
  if (siteId.includes('\n') || instanceId.includes('\n')) throw new Error('mintFormId: siteId and instanceId must not contain a newline');
  const payload = encodePayload(siteId, instanceId);
  const signature = signPayload(secret, payload);
  const token = `${Buffer.from(payload, 'utf-8').toString('base64url')}.${signature.toString('base64url')}`;
  if (token.length > MAX_FORM_ID_TOKEN_LENGTH) {
    throw new Error(`mintFormId: token length ${token.length} exceeds MAX_FORM_ID_TOKEN_LENGTH ${MAX_FORM_ID_TOKEN_LENGTH}`);
  }
  return token;
}

function decodeBase64url(segment: string): Buffer | null {
  return BASE64URL_PATTERN.test(segment) ? Buffer.from(segment, 'base64url') : null;
}

/** Rejects a length mismatch BEFORE calling `timingSafeEqual`, which throws
 *  on unequal-length buffers rather than returning false. */
function signaturesMatch(expected: Buffer, actual: Buffer): boolean {
  return expected.length === actual.length && timingSafeEqual(expected, actual);
}

function parsePayload(payload: string): VerifiedFormId | null {
  const [siteId, instanceId, ...rest] = payload.split('\n');
  if (!siteId || !instanceId || rest.length > 0) return null;
  return { siteId, instanceId };
}

/**
 * Verify a token minted by `mintFormId`. Every failure mode — oversized,
 * malformed, tampered payload/signature, wrong secret — returns `null`; the
 * `/public/forms` route maps `null` to the SAME opaque 404 'unknown-form' it
 * returns for an unresolvable site or an unknown/not-a-form instance, so a
 * bot can never distinguish a bad token from a non-existent form (no
 * enumeration oracle), let alone learn WHY a token was rejected.
 */
export function verifyFormId(secret: string, token: string): VerifiedFormId | null {
  if (token.length === 0 || token.length > MAX_FORM_ID_TOKEN_LENGTH) return null;
  const parts = token.split('.');
  if (parts.length !== 2) return null;
  const [payloadPart, sigPart] = parts;
  if (!payloadPart || !sigPart) return null;
  const payloadBuf = decodeBase64url(payloadPart);
  const sigBuf = decodeBase64url(sigPart);
  if (!payloadBuf || !sigBuf) return null;
  const payload = payloadBuf.toString('utf-8');
  const expected = signPayload(secret, payload);
  if (!signaturesMatch(expected, sigBuf)) return null;
  return parsePayload(payload);
}
